// @ts-check
// ── Messages: start-up at sign-in, clean-up at sign-out ───────────
//
// The one place the CRM's messaging starts and stops. Both boot paths in app.js
// call startMessaging (the owner's directly, a team member's with the inbox
// permission through its per-feed `start`), and sign-out calls stopMessaging.
//
//   startMessaging(run?)  the saved state for this person, the Inbox's live
//                         feed, its conversation list and poll, then the corner
//   stopMessaging()       the Inbox's clean-up, the corner taken down, and the
//                         saved state emptied
//
// ⚠️ EACH STEP GOES TO THE RUNNER ON ITS OWN. A team member's boot isolates
// every feed (`start(label, fn)` catches a throw), so one failing does not take
// the rest of their boot with it. The Inbox's labels are the ones that boot
// logged before these calls moved here. With no runner the steps run directly,
// as the owner's boot always ran them (inside its own try).
//
// ⚠️ THE SAVED STATE IS THE SIGNED-IN PERSON'S. Its key carries their own id (a
// team member's, not the owner's), so one person never reads another's open
// chats or drafts in a shared browser tab, and sign-out empties it.

import { initInboxRealtime, loadInboxConversations, startInboxConvPoll, cleanupInbox } from '../inbox.js';
import { currentUser } from '../state.js';
import { initSavedState, clearSaved } from './saved-state.js';
import { initCornerMessages, teardownCornerMessages } from '../corner-messages/index.js';

/** @typedef {(label: string, fn: () => unknown) => void} StepRunner */

/** @type {StepRunner} */
const runDirectly = (_label, fn) => { fn(); };

/**
 * Start messaging for the signed-in person.
 * @param {StepRunner} [run] runs each step (the member boot's `start`)
 */
export function startMessaging(run = runDirectly) {
  run('saved state', () => initSavedState(/** @type {any} */ (currentUser)?.id));
  run('inbox live feed', () => initInboxRealtime());
  run('inbox conversations', () => loadInboxConversations());
  run('inbox poll', () => startInboxConvPoll());
  run('corner messages', () => initCornerMessages());
}

/** Sign-out: stop every feed and poll, take the corner down, forget what the page held. */
export function stopMessaging() {
  cleanupInbox();
  teardownCornerMessages();
  clearSaved();
}
